import { useEffect, useRef, useState } from 'react'

/**
 * Shows the value immediately on first render.
 * On subsequent value changes, animates from the old value to the new one
 * with a snappy ease-out and a brief green flash — matching the Shopify
 * live-metrics feel without the fake "count from zero" gimmick.
 *
 * @param {number}   value     - Target numeric value.
 * @param {function} formatter - Formats the animated number for display.
 * @param {number}   duration  - Animation duration in ms (default 700).
 */
export default function AnimatedNumber({ value, formatter, duration = 700 }) {
    const target = parseFloat(value) || 0
    const isFirstRender = useRef(true)
    const fromRef = useRef(target)
    const rafRef = useRef(null)
    const [display, setDisplay] = useState(target)
    const [flash, setFlash] = useState(false)
    const flashTimer = useRef(null)

    useEffect(() => {
        // Skip animation on first mount — show the real number immediately
        if (isFirstRender.current) {
            isFirstRender.current = false
            fromRef.current = target
            setDisplay(target)
            return
        }

        const from = fromRef.current
        const to = target
        if (from === to) return

        if (rafRef.current) cancelAnimationFrame(rafRef.current)
        if (flashTimer.current) clearTimeout(flashTimer.current)

        // Green flash on update
        setFlash(true)
        flashTimer.current = setTimeout(() => setFlash(false), 900)

        let startTime = null
        const step = (timestamp) => {
            if (!startTime) startTime = timestamp
            const progress = Math.min((timestamp - startTime) / duration, 1)
            // ease-out quart: fast start, soft landing
            const eased = 1 - Math.pow(1 - progress, 4)
            setDisplay(from + (to - from) * eased)
            if (progress < 1) {
                rafRef.current = requestAnimationFrame(step)
            } else {
                setDisplay(to)
                fromRef.current = to
            }
        }

        rafRef.current = requestAnimationFrame(step)
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current)
            if (flashTimer.current) clearTimeout(flashTimer.current)
        }
    }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

    const formatted = formatter ? formatter(display) : String(Math.round(display))

    return (
        <span style={{
            transition: 'color 0.6s ease',
            color: flash ? '#38A169' : 'inherit',
        }}>
            {formatted}
        </span>
    )
}
